import React, { useState, useEffect } from 'react';
import Button from './Button';
import api from '../utils/api';

const QuestionTagModal = ({ isOpen, onClose, questionId, availableTags, currentTags = [], onTagsUpdated }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedTags(currentTags.map(t => t.id));
    }
  }, [isOpen, currentTags]);

  const handleToggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSave = async () => {
    setUpdating(true);
    try {
      const currentTagIds = currentTags.map(t => t.id);
      
      // Find tags to add and remove
      const tagsToAdd = selectedTags.filter(id => !currentTagIds.includes(id));
      const tagsToRemove = currentTagIds.filter(id => !selectedTags.includes(id));

      // Add new tags
      await Promise.all(
        tagsToAdd.map(tagId => 
          api.post(`/api/tags/question/${questionId}/tag/${tagId}`)
        )
      );

      // Remove unselected tags
      await Promise.all(
        tagsToRemove.map(tagId => 
          api.delete(`/api/tags/question/${questionId}/tag/${tagId}`)
        )
      );

      onTagsUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating question tags:', error);
      alert('Failed to update tags');
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Manage Question Tags</h2>
              <p className="text-gray-600 text-sm mt-1">Select tags for this question</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none p-2 rounded-full hover:bg-gray-100"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tag Selection */}
          <div className="mb-6">
            {availableTags.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <p>No tags available.</p>
                <p className="text-sm mt-1">Create tags first using "Manage Tags"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableTags.map(tag => (
                  <button
                    key={tag.id}
                    onClick={() => handleToggleTag(tag.id)}
                    className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                      selectedTags.includes(tag.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="font-medium text-gray-900">{tag.name}</span>
                    </div>
                    {selectedTags.includes(tag.id) && (
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={updating}
              size="sm"
            >
              {updating ? 'Saving...' : 'Save Tags'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionTagModal;

